import { useState } from "react"
import { ExpenseModel } from "../../../../../reduxToolkit/expensesSlice"
import { formatDateInTimeZone } from "../../../../../../lib/formatDateInTimeZone"
import { formatCurrency } from "../../../../../../lib/formatCurrency"
import { present } from "../../../../../../lib/util/present"
import { CopyableSpan } from "../../../../../components/copyableSpan"
import { Decimal } from "decimal.js"
import { maybeCalculateActualPaidAmountFromIncompleteExpense, maybeCalculateDiscountAmountFromIncompleteExpense, maybeCalculateRemainingOwedAmountFromIncompleteExpense } from "../../../../../../lib/expenses/amounts"

import './submissionExpenseList.scss'

interface SubmissionExpenseListProps {
  expenses: ExpenseModel[]
}

export function SubmissionExpenseList({ expenses }: SubmissionExpenseListProps) {
  return (
    <div className="submission-expense-list">
      {expenses.slice().sort(byDateNullsFirst).map((expense) => (
        <SubmissionExpenseRow key={expense.id} expense={expense} />
      ))}
    </div>
  )
}

interface SubmissionExpenseRowProps {
  expense: ExpenseModel
}

function SubmissionExpenseRow({ expense }: SubmissionExpenseRowProps) {
  const { id, date, paidAmount, provider } = expense
  const [expanded, setExpanded] = useState(true)

  const classes = [
    'submission-expense-list__row col-12 card',
    expanded && 'expanded',
  ].filter(present)

  return (
    <div id={`submission-expense-row_${id}`} className="row">
      <div className={classes.join(' ')}>
        <div className="card-body">
          <div
            className={`submission-expense-list__row-header ${expanded && 'expanded'}`}
            onClick={!expanded ? ((e) => { e.preventDefault(); e.stopPropagation(); setExpanded(true) }) : undefined}
          >
            <div className="card-title">
              <h5>
                {paidAmount ? formatCurrency(paidAmount) : 'Unknown Amount'}
              </h5>
            </div>

            <button
              className="btn btn-link submission-expense-list__row-collapse-toggle"
              aria-expanded={expanded}
              aria-controls={`#submission-expense-${id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(!expanded) }}
            >
              <i className="material-icons">{expanded ? 'expand_less' : 'expand_more'}</i>
            </button>
          </div>

          <div
            className={`submission-expense-list__row-collapse collapse ${expanded && 'show'} card-text`}
            id={`submission-expense-${id}`}
            data-id={id}
          >
            <SubmissionExpenseWidget expense={expense} expanded={expanded} />
          </div>
        </div>
      </div>
    </div>
  )
}

interface SubmissionExpenseWidgetProps {
  expense: ExpenseModel
  expanded?: boolean
}

function SubmissionExpenseWidget({ expense }: SubmissionExpenseWidgetProps) {
  const { date, listedAmount, provider } = expense

  const discountAmount = maybeCalculateDiscountAmountFromIncompleteExpense(expense)
  const paidAmount = maybeCalculateActualPaidAmountFromIncompleteExpense(expense)
  const remainingOwedAmount = maybeCalculateRemainingOwedAmountFromIncompleteExpense(expense)

  return (
    <div className="submission-expense-list__widget">
      {present(provider) && (
        <div className="submission-expense-list__widget-provider">
          <h6>Provider</h6>
          <CopyableSpan>{provider}</CopyableSpan>
        </div>
      )}

      <div className="submission-expense-list__widget-details">
        <h6>Details</h6>
        <table className="submission-expense-list__details-table">
          <tbody>
            {date && (
              <tr className="submission-expense-list__details-row">
                <td className="submission-expense-list__details-label">Date</td>
                <td className="submission-expense-list__details-value">
                  <CopyableSpan>{formatDateInTimeZone(date, { format: 'MM/dd/yyyy' })}</CopyableSpan>
                </td>
              </tr>
            )}
            {listedAmount && (
              <tr className="submission-expense-list__details-row">
                <td className="submission-expense-list__details-label">Listed</td>
                <td className="submission-expense-list__details-value">
                  <CopyableSpan>{formatCurrency(new Decimal(listedAmount))}</CopyableSpan>
                </td>
              </tr>
            )}
            {discountAmount && (
              <tr className="submission-expense-list__details-row">
                <td className="submission-expense-list__details-label">Discount</td>
                <td className="submission-expense-list__details-value">
                  <CopyableSpan>{formatCurrency(discountAmount)}</CopyableSpan>
                </td>
              </tr>
            )}
            {paidAmount && (
              <tr className="submission-expense-list__details-row submission-expense-list__details-row--paid">
                <td className="submission-expense-list__details-label">Paid</td>
                <td className="submission-expense-list__details-value">
                  <CopyableSpan>{formatCurrency(paidAmount)}</CopyableSpan>
                </td>
              </tr>
            )}
            {remainingOwedAmount && (
              <tr className={`submission-expense-list__details-row ${!remainingOwedAmount.eq(0) ? 'submission-expense-list__details-row--owed' : ''}`}>
                <td className="submission-expense-list__details-label">Owed</td>
                <td className="submission-expense-list__details-value">
                  <CopyableSpan>{formatCurrency(remainingOwedAmount)}</CopyableSpan>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function byDateNullsFirst(a: { date?: string | null }, b: { date?: string | null }) {
  if (!present(a.date)) {
    return -1
  }
  if (!present(b.date)) {
    return 1
  }

  return Date.parse(b.date) - Date.parse(a.date)
}
